import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageToggle from '../../components/LanguageToggle';
import VocationWorksLogo from '../../components/VocationWorksLogo';

const NAVY     = '#0D1B2A';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// Supabase's password-recovery redirect lands here with the recovery tokens in the URL
// hash (not a query param), which the client library parses automatically on load
// (detectSessionInUrl, on by default) and turns into a real session plus a
// PASSWORD_RECOVERY auth event -- there's no separate "recovery code" to read ourselves.
// That parsing is async and can race this component's mount, so we both listen for the
// event AND check for an already-established session, and only declare the link invalid
// once neither has shown up after a short grace period.
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [status, setStatus] = useState('verifying'); // verifying | ready | invalid | done
  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]           = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY') setStatus('ready');
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session) setStatus(current => (current === 'verifying' ? 'ready' : current));
    });

    const timeout = setTimeout(() => {
      if (cancelled) return;
      setStatus(current => (current === 'verifying' ? 'invalid' : current));
    }, 3000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      listener?.subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { background-color: #F5F6F8; }
      .reset-password-input::placeholder { color: rgba(0,0,0,0.3); }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError(t('union.signup.step1.requiredFieldsAlert'));
      return;
    }
    if (password.length < 8) {
      setError(t('union.signup.step1.passwordLengthAlert'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('employer.signup.step1.passwordMismatchAlert'));
      return;
    }

    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    setStatus('done');
  };

  const inputStyle = {
    width: '100%',
    padding: '11px 14px',
    fontSize: '0.9375rem',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    outline: 'none',
    fontFamily: FONT_BODY,
    color: NAVY,
    backgroundColor: WHITE,
  };

  return (
    <div style={{ fontFamily: FONT_BODY, minHeight: '100dvh', backgroundColor: '#F5F6F8', display: 'flex', flexDirection: 'column' }}>

      <nav style={{
        height: '60px',
        backgroundColor: NAVY,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 2rem',
        flexShrink: 0,
      }}>
        <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={36} />
        </div>
        <LanguageToggle />
      </nav>

      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1.25rem',
      }}>
        <div style={{
          backgroundColor: WHITE,
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(13,27,42,0.10)',
          padding: '40px',
          width: '100%',
          maxWidth: '400px',
          textAlign: status === 'verifying' || status === 'invalid' || status === 'done' ? 'center' : 'left',
        }}>

          {status === 'verifying' && (
            <p style={{ fontSize: '0.95rem', color: 'rgba(13,27,42,0.6)' }}>
              {t('auth.resetPassword.verifyingLink')}
            </p>
          )}

          {status === 'invalid' && (
            <>
              <h1 style={{ fontFamily: FONT_BODY, fontSize: '22px', fontWeight: 700, color: NAVY, marginBottom: '10px' }}>
                {t('auth.resetPassword.invalidHeading')}
              </h1>
              <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.55)', marginBottom: '24px', lineHeight: 1.5 }}>
                {t('auth.resetPassword.invalidBody')}
              </p>
              <button
                onClick={() => navigate('/login')}
                style={{
                  backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px',
                  padding: '13px 30px', fontWeight: 700, fontSize: '0.95rem',
                  cursor: 'pointer', fontFamily: FONT_BODY,
                }}
              >
                {t('auth.resetPassword.goToLoginButton')}
              </button>
            </>
          )}

          {status === 'done' && (
            <>
              <h1 style={{ fontFamily: FONT_BODY, fontSize: '22px', fontWeight: 700, color: NAVY, marginBottom: '10px' }}>
                {t('auth.resetPassword.successHeading')}
              </h1>
              <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.55)', marginBottom: '24px', lineHeight: 1.5 }}>
                {t('auth.resetPassword.successBody')}
              </p>
              <button
                onClick={() => navigate('/login')}
                style={{
                  backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px',
                  padding: '13px 30px', fontWeight: 700, fontSize: '0.95rem',
                  cursor: 'pointer', fontFamily: FONT_BODY,
                }}
              >
                {t('auth.resetPassword.goToLoginButton')}
              </button>
            </>
          )}

          {status === 'ready' && (
            <>
              <h1 style={{ fontFamily: FONT_BODY, fontSize: '24px', fontWeight: 700, color: NAVY, marginBottom: '6px' }}>
                {t('auth.resetPassword.heading')}
              </h1>
              <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.5)', marginBottom: '24px' }}>
                {t('auth.resetPassword.subtitle')}
              </p>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: NAVY, marginBottom: '6px' }}>
                    {t('auth.resetPassword.newPasswordLabel')}
                  </label>
                  <input
                    className="reset-password-input"
                    type="password"
                    placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    style={inputStyle}
                    autoFocus
                  />
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: NAVY, marginBottom: '6px' }}>
                    {t('auth.resetPassword.confirmPasswordLabel')}
                  </label>
                  <input
                    className="reset-password-input"
                    type="password"
                    placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                {error && (
                  <p style={{ fontSize: '0.85rem', color: '#DC2626', marginBottom: '16px' }}>{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '13px',
                    backgroundColor: AMBER,
                    color: NAVY,
                    border: 'none',
                    borderRadius: '8px',
                    fontFamily: FONT_BODY,
                    fontSize: '0.9375rem',
                    fontWeight: 700,
                    cursor: submitting ? 'default' : 'pointer',
                    opacity: submitting ? 0.7 : 1,
                  }}
                >
                  {submitting ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submitButton')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
